<template>
    <div>
        <a-row type="flex" justify="end" class="mb-15">
            <a-dropdown v-model="visibleDropdown">
                <a-button icon="eye">{{ lang.orders.columns }}</a-button>
                <a-menu slot="overlay">
                    <a-menu-item
                        v-for="(field, key) in fields.filter(f => f.dataIndex !== 'url')"
                        :key="key">
                        <a-checkbox v-model="columns[key]" :value="field.dataIndex">
                            {{ field.title }} <a-icon v-show="!columns[key]" type="eye-invisible"/>
                        </a-checkbox>
                    </a-menu-item>
                </a-menu>
            </a-dropdown>
        </a-row>
        <a-table
            bordered
            size="middle"
            :columns="fields.filter((field, key) => columns[key])"
            :loading="isLoading"
            :data-source="paginated.data"
            :pagination="paginated"
            :row-key="(record, index) => index"
            @change="handleTableChanges"
            :custom-row="customRow"
            :row-selection="rowSelection"
        >
            <!-- ------------------------------
                TABLE TITLE & REFRESH BUTTON
            ------------------------------- -->
            <template #title>
                <a-row type="flex" align="middle" justify="space-between">
                    <a-col class="d-flex align-items-center">
                        <h2 class="mb-0 mr-2">{{ lang.orders.requests }}</h2>
                        <template v-if="selection.orders.length > 0">
                            <a-button class="mr-1">
                                <a :href="`${url}/get-file-order?id=${selection.orders.join(',')}`" target="_blank">Descargar pedidos</a>
                            </a-button>
                            <a-dropdown :trigger="['click']">
                                <a-button>
                                    Cambiar estado
                                </a-button>
                                <a-menu slot="overlay">
                                    <template v-if="loggedUserIsAdmin">
                                        <a-menu-divider />
                                        <a-menu-item
                                            v-for="(status, value) in unrejectedOrders"
                                            @click="handleMultipleSelection(value)"
                                            :key="value">
                                            <a-badge :status="color(value)" /> {{ status[value] }}
                                        </a-menu-item>
                                        <!-- <a-menu-divider /> -->
                                        <!-- <a-menu-item key="4" @click="deleteOrder(id)">
                                            <a-icon type="delete" :style="{ color: '#f5222d' }" /> {{ lang.orders.delete }}
                                        </a-menu-item> -->
                                    </template>
                                </a-menu>
                            </a-dropdown>
                        </template>
                    </a-col>
                    <a-col>
                        <a-row type="flex" :gutter="8">
                            <a-col class="my-sm-2 w-sm-100p">
                                <a-tooltip placement="top">
                                    <a-input-search
                                        ref="client-search"
                                        v-model="search.client"
                                        :placeholder="lang.orders.clientId_placeholder"
                                        @search="getOrder({ id: $event, page: 1, type: 'client' }).then(() => clearSearch('client'))"
                                        enter-button/>
                                </a-tooltip>
                            </a-col>
                            <a-col class="my-sm-2 w-sm-100p">
                                <a-tooltip placement="top">
                                    <a-input-search
                                        ref="order-search"
                                        v-model="search.order"
                                        :placeholder="lang.orders.order_placeholder"
                                        @search="getOrder({ id: $event, page: 1, type: 'order' }).then(() => clearSearch('order'))"
                                        enter-button/>
                                </a-tooltip>
                            </a-col>
                            <a-col class="my-sm-2 w-sm-100p">
                                <a-button block @click="fetchOrders({ force: true }, true)">
                                    {{ lang.orders.refresh }}
                                </a-button>
                            </a-col>
                            <a-dropdown :trigger="['click']">
                                <a-button>Filtrar por estado</a-button>
                                <a-menu slot="overlay">
                                    <a-menu-item @click="fetchOrders({ page: 1, force: true })">
                                        Mostrar todos los estados
                                    </a-menu-item>
                                    <template v-for="(attribute, value) in lang.orders.filter_status_codes">
                                        <a-menu-item :key="value" @click="fetchOrders({ page: 1, status: value, force: true })">
                                            <a-badge :status="color(value)" /> {{ attribute }}
                                        </a-menu-item>
                                    </template>
                                </a-menu>
                            </a-dropdown>
                        </a-row>
                    </a-col>
                </a-row>
            </template>

            <!-- ------------------------------
                        CHECKBOX
            ------------------------------- -->
            <!-- <template #selection="id">
                <input type="checkbox" :value="id" v-model="selection.orders" />
            </template> -->

            <!-- ------------------------------
                    SEE DETAILS BUTTON
            ------------------------------- -->
            <template #id="id">
                <router-link :to="`/orders/${id}`" class="ghost-link">
                    {{ lang.orders.see_details }}
                </router-link>
            </template>

            <!-- ------------------------------
                    STATUS WITH BADGE
            ------------------------------- -->
            <template #status="status">
                <a-badge :status="color(status)" /> {{ lang.orders.status_codes[status] }}
            </template>

            <template #created_at="date">
                {{ new Date(date).toLocaleDateString() }}
            </template>

            <!-- ------------------------------
                    OPTIONS DROPDOWN
            ------------------------------- -->
            <template #status_change="id, order">
                <a-dropdown :trigger="['click']">
                    <a-button icon="ellipsis" shape="circle"/>
                    <a-menu slot="overlay">
                        <a-menu-item key="5" @click="handleOrder(id, order)">
                            {{ lang.orders.see_details }}
                        </a-menu-item>
                        <template v-if="loggedUserIsAdmin">
                            <a-menu-divider />
                            <template v-for="(attribute, value) in lang.orders.mark_status_codes">
                                <a-menu-item :key="value" @click="handle($event.key, id, order)" v-if="parseInt(value) !== parseInt(order.status)">
                                    <a-badge :status="color(value)" /> {{ attribute }}
                                </a-menu-item>
                            </template>
                            <template v-if="order.status !== 1">
                                <a-menu-divider />
                                <a-menu-item key="4" @click="deleteOrder(id)">
                                    <a-icon type="delete" :style="{ color: '#f5222d' }" /> {{ lang.orders.delete }}
                                </a-menu-item>
                            </template>
                        </template>
                    </a-menu>
                </a-dropdown>
            </template>
        </a-table>

        <a-modal title="¿Por qué desea rechazar este pedido?" :visible="visibleReason" @ok="updateOrder({ id: order.id, order })" @cancel="visibleReason = false">
            <label for="reason">Razón de rechazo</label>
            <a-input id="reason" v-model="order.reason" />
        </a-modal>

        <a-modal title="Aviso" :visible="emailModalVisible" @ok="toggleModalVisible" @cancel="toggleModalVisible">
            <p>Aqui se deberia enviar un correo.</p>
            <p>No se envia porque no nos han mandado las credenciales necesarias para configurar el correo</p>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
import {URL} from '@/constants'
export default {
    data: () => ({
        url: URL,
        allowCustomRow: false,
        columns: [],
        visibleDropdown: false,
        visibleReason: false,
        search: {
            client: '',
            order: ''
        },
        order: {
            reason: '',
            status: 0,
            items: []
        },
        selection: {
            orders: [],
            status: 0
        },
        filtered: false
    }),
    watch: {
        isLoading(val) {
            if (!val) {
                this.visibleReason = false
            }
        }
    },
    computed: {
        ...mapState({
            paginated: state => state.order.paginated,
            isLoading: state => state.order.isLoading,
            emailModalVisible: state => state.order.emailModalVisible
        }),
        ...mapGetters({
            fields: 'order/fields',
            lang: 'lang',
            loggedUserIsAdmin: 'session/loggedUserIsAdmin',
            color: 'order/color'
        }),
        unrejectedOrders() {
            return Object.entries(this.lang.orders.mark_status_codes)
                .filter(el => el[0] !== "3")
                .map(el => ({[`${el[0]}`]: el[1]}))
        },
        rowSelection() {
            if (this.filtered && this.loggedUserIsAdmin) {
                return {
                    onChange: (selectedRowKeys, selectedRows) => {
                        this.selection.orders = selectedRows.map($0 => $0.id) // eslint-disable-line vue/no-side-effects-in-computed-properties
                        // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
                    },
                    // onSelect: (record, selected, selectedRows) => {
                    //     // console.log(record, selected, selectedRows);
                    // },
                    // onSelectAll: (selected, selectedRows, changeRows) => {
                    //     this.selection.orders = selectedRows.map($0 => $0.id) // eslint-disable-line vue/no-side-effects-in-computed-properties
                    // },
                    // getCheckboxProps: record => ({
                    //     props: {
                    //         disabled: !,
                    //         checked: this.selection.orders.includes(record.id)
                    //     },
                    // }),
                }
            }

            return null
        }
    },
    methods: {
        ...mapActions({
            fetchOrdersProvider: 'order/fetchOrders',
            fetchOrder: 'order/fetchOrder',
            updateOrder: 'order/updateOrder',
            deleteOrder: 'order/deleteOrder',
            updateMultipleOrdersStatus: 'order/updateMultipleOrdersStatus',
            getClient: 'order/getClient',
            getOrder: 'order/getOrder'
        }),
        ...mapMutations({
            toggleModalVisible: 'order/SET_EMAIL_MESSAGE_VISIBLE'
        }),
        fetchOrders(query, refresh) {
            if (refresh) {
                const query = Object.assign({}, this.$route.query);
                delete query.id;
                delete query.type;
                this.$router.replace({ query });
            }
            let customQuery = {}
            if (this.$route.query.type && this.$route.query.id) {
                if (this.$route.query.type === 'order') {
                    customQuery = {
                        type: this.$route.query.type,
                        order: this.$route.query.id
                    }
                } else {
                    customQuery = {
                        type: this.$route.query.type,
                        client: this.$route.query.id
                    }
                }
            }
            this.fetchOrdersProvider({ ...query, ...customQuery })
                .then(() => {
                    this.filtered = false
                    setTimeout(() => {
                        this.filtered = query.status && query.status !== ''
                        this.selection.orders = []
                    }, 1)
                })
        },
        handleTableChanges(pagination, filters, sorter) {
            this.fetchOrders({ page: pagination.current, force: true, sorter })
        },
        handleOrder(id, order) {
            this.fetchOrder({ order });
            this.$router.push(`/orders/${id}`);
        },
        handle(status, id, order) {
            const currentStatus = parseInt(status)
            if (currentStatus === 3 || currentStatus === 2) {
                this.order = { ...order, status, items: JSON.parse(order.items) };
                this.visibleReason = true;
            } else {
                this.updateOrder({
                    id,
                    order: {
                        ...order,
                        status,
                        items: JSON.parse(order.items)
                    }
                })
            }
        },
        clearSearch(type) {
            this.search[type] = ''
        },
        customRow(order) {
            return this.allowCustomRow ? {
                class: `${this.color(order.status)}-bg`
            } : {}
        },
        handleMultipleSelection(value) {
            this.updateMultipleOrdersStatus({ orders: this.selection.orders, status: value })
        }
    },
    mounted() {
        this.fetchOrders({ force: false, page: this.$route.query.page, status: 0 });
        this.columns = this.fields.filter(f => f.dataIndex !== 'url').map(() => true)
    }
}
</script>
